import type { ComponentType } from "react";
import { useEffect } from "react";

import { Navigate, useLocation } from "react-router-dom";

import Routers from "../constants/routers";
import { useAuth } from "../context/AuthContext";

export const notAuth = <P extends object>(WrappedComponent: ComponentType<P>) => {
  const AuthenticatedComponent: React.FC<P> = (props) => {
    const { authStatus } = useAuth();

    if (authStatus === "loading") {
      return null;
    }

    if (authStatus !== "authenticated") {
      return <WrappedComponent {...props} />;
    }

    return <Navigate to={Routers.Dashboard} replace />;
  };

  return AuthenticatedComponent;
};

interface WithAuthOptions {
  allowIncompleteBrokerProfile?: boolean;
}

export const withAuth = <P extends object>(WrappedComponent: ComponentType<P>, options: WithAuthOptions = {}) => {
  const AuthenticatedComponent: React.FC<P> = (props) => {
    const { authStatus, fetchUser, hasToken, user } = useAuth();
    const location = useLocation();

    useEffect(() => {
      if (hasToken && authStatus === "unauthenticated") {
        void fetchUser();
      }
    }, [authStatus, fetchUser, hasToken]);

    if (authStatus === "loading" || (hasToken && authStatus === "unauthenticated")) {
      return null;
    }

    if (authStatus === "authenticated") {
      const isBrokerAccount = user?.account_type === "broker" || user?.affiliate_type === "broker";
      const shouldCompleteBrokerProfile = isBrokerAccount && user?.broker_profile_completed === false;

      if (shouldCompleteBrokerProfile && !options.allowIncompleteBrokerProfile) {
        return <Navigate to={Routers.BrokerProfileSetup} state={{ from: location }} replace />;
      }

      return <WrappedComponent {...props} />;
    }

    return <Navigate to={Routers.Login} state={{ from: location }} replace />;
  };

  return AuthenticatedComponent;
};
